'use client';

import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import { Compass } from 'lucide-react';
import { useBoards } from '@/lib/community/hooks/use-boards';
import type { CommunityBoard, AbilityType } from '@/lib/community/types';

/** 判断是否为 emoji */
function isEmoji(s: string): boolean {
	return /^\p{Emoji_Presentation}/u.test(s);
}

/** 将后端 icon 字段解析为 lucide IconName，无效值返回 null */
function resolveIconName(raw: string): IconName | null {
	if (!raw || isEmoji(raw)) return null;
	const kebab = raw.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
	return kebab as IconName;
}

/** Board category labels */
const CATEGORY_LABELS: Record<CommunityBoard['category'], string> = {
	scenario: '主题版',
	type: '类型',
	special: '特色版块',
};

/**
 * BoardSidebar — left sidebar listing community boards.
 *
 * Juejin-style transparent navigation list (no card wrapper).
 * Parent is responsible for placing it in a sticky aside within the layout.
 */
export function BoardSidebar({
	activeBoardSlug,
	activeType,
	onSelectBoard,
	onSelectType,
}: {
	activeBoardSlug: string | null;
	activeType?: AbilityType;
	onSelectBoard: (slug: string | null) => void;
	onSelectType?: (type: AbilityType | undefined) => void;
}) {
	const { boards, loading } = useBoards();

	const handleSelectAll = () => {
		onSelectBoard(null);
		onSelectType?.(undefined);
	};

	const handleBoardClick = (board: CommunityBoard) => {
		if (board.abilityType) {
			onSelectBoard(null);
			onSelectType?.(board.abilityType);
		} else {
			onSelectType?.(undefined);
			onSelectBoard(board.slug);
		}
	};

	const categories: CommunityBoard['category'][] = ['scenario'];

	const isAllActive = activeBoardSlug === null && !activeType;

	return (
		<div className="space-y-1">
			{/* "All" option — first item, aligns with center tabs */}
			<button
				className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left text-sm transition-all duration-200 ${
					isAllActive ? 'bg-primary/10 text-primary font-semibold' : 'text-foreground/70 hover:text-foreground hover:bg-foreground/[0.04]'
				}`}
				onClick={handleSelectAll}
				type="button"
			>
				<Compass className="h-4 w-4" />
				<span className="text-xs font-semibold">综合</span>
				{isAllActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />}
			</button>

			{/* Board list by category */}
			<div className="overflow-y-auto space-y-2 no-scrollbar">
				{loading ? (
					[0, 1, 2].map((group) => (
						<div key={group}>
							<div className="px-3 py-1">
								<div className="h-2 w-10 rounded bg-foreground/8 animate-pulse" />
							</div>
							<div className="space-y-0.5">
								{Array.from({ length: group === 0 ? 4 : group === 1 ? 3 : 1 }).map((_, i) => (
									<div className="flex items-center gap-2.5 px-3 py-1.5" key={i}>
										<div className="h-4 w-4 rounded bg-foreground/8 animate-pulse shrink-0" />
										<div className="flex-1">
											<div className="h-3 w-20 rounded bg-foreground/8 animate-pulse" />
										</div>
									</div>
								))}
							</div>
						</div>
					))
				) : boards.length === 0 ? (
					<div className="px-3 py-6 text-center">
						<p className="text-sm text-muted-foreground">暂无主题版块</p>
						<p className="text-xs text-muted-foreground/60 mt-1">版块正在建设中...</p>
					</div>
				) : (
					categories.map((cat) => {
						const filtered = boards.filter((b) => b.category === cat);
						if (filtered.length === 0) return null;
						return (
							<div key={cat}>
								<p className="px-3 pt-1.5 pb-1 text-caption font-semibold text-muted-foreground/50 uppercase tracking-widest">{CATEGORY_LABELS[cat]}</p>
								<div className="space-y-px">
									{filtered.map((board) => {
										const iconName = resolveIconName(board.icon);
										const isActive = board.abilityType ? activeType === board.abilityType : activeBoardSlug === board.slug;
										return (
											<button
												className={`w-full flex items-center gap-2.5 px-3 py-[7px] rounded-xl text-left transition-all duration-200 group/item ${
													isActive ? 'bg-foreground/[0.06] text-foreground font-semibold' : 'text-foreground/65 hover:text-foreground hover:bg-foreground/[0.04]'
												}`}
												key={board.slug}
												onClick={() => handleBoardClick(board)}
												type="button"
											>
												{/* Icon */}
												<span
													className={`flex items-center justify-center w-5 h-5 rounded-md transition-colors ${
														isActive ? 'bg-foreground/10' : 'bg-foreground/[0.04] group-hover/item:bg-foreground/[0.07]'
													}`}
												>
													{iconName ? (
														<DynamicIcon className="h-3 w-3" name={iconName} />
													) : board.icon && isEmoji(board.icon) ? (
														<span className="text-xs leading-none">{board.icon}</span>
													) : (
														<span className="text-xs leading-none">📌</span>
													)}
												</span>

												{/* Name */}
												<span className="text-xs font-medium truncate flex-1">{board.name}</span>
											</button>
										);
									})}
								</div>
							</div>
						);
					})
				)}
			</div>
		</div>
	);
}
